import React from "react";

import type { IconButtonProps } from "@braintrust/braintrust-ui-components";
import { IconButton, Menu } from "@braintrust/braintrust-ui-components";
import {
  MoreVertIcon,
  ThumbDownEmojiIcon,
} from "@braintrust/braintrust-ui-components/Icons";
import { useChangeProposalSection } from "@js/apps/admin/hooks/change-proposal-section";
import { useIsNodeStaff } from "@js/apps/common/hooks/is-node-staff";
import { useIsNodeOrImpersonated } from "@js/apps/common/hooks/use-is-node-or-impersonated";
import { useUser } from "@js/apps/common/hooks/use-user";
import { shareApplicantClicked } from "@js/apps/employer/actions";
import { openShareBidsWithTeamMembersModal } from "@js/apps/employer/components/share-bids-with-team-members-modal";
import { useCanShareWithTeamMembers } from "@js/apps/employer/hooks/use-can-share-with-team-members";
import { isPublicBidWithoutUser } from "@js/apps/jobs/apps/bids/helpers";
import { useBidsContext } from "@js/apps/jobs/context/refetch-bids-context";
import { getMakeOfferUrl } from "@js/apps/jobs/utils";
import { RouterLink } from "@js/components/link";
import { Snackbar } from "@js/components/snackbar";
import { useAppDispatch } from "@js/hooks/redux";
import type { EmployerBidListBid, Job } from "@js/types/jobs";

import {
  useRequestIDVMutation,
  useUnRejectBidMutation,
  useUpdateMatcherRecommendationMutation,
} from "../../api";
import { useSubmitSchedulingLink } from "../../hooks/use-submit-scheduling-link";
import { openRejectOfferMessageModal } from "../bid-messages-modals";
import { openMakeOfferMessageModal } from "../make-offer-message";
import { openOptionalFeesModal } from "../optional-fees-modal";
import { setIndexToScrollTo } from "../virtualized-job-bid-list/rendered-item-index";

import { hideApplication } from "./hide-application";
import { openMarkAsHiredConfirmationModal } from "./mark-as-hired-confirmation-modal";
import { openSendRetryConfirmationModal } from "./send-retry-confirmation-modal";
import { SendSMSMessageItem } from "./send-sms-message-item";

type BidActionsMenuProps = {
  bid: EmployerBidListBid;
  job: Job;
  sx?: IconButtonProps["sx"];
  closeDrawer?: () => void;
  includeMakeOfferAction?: boolean;
  index?: number;
};

export const BidActionsMenu = ({
  bid,
  job,
  sx,
  closeDrawer,
  index,
  includeMakeOfferAction = false,
}: BidActionsMenuProps) => {
  const dispatch = useAppDispatch();
  const user = useUser();
  const isNodeOrImpersonated = useIsNodeOrImpersonated();
  const isNodeStaff = useIsNodeStaff();
  const isFreelanceHired = bid.status === ENUMS.BidStatus.HIRED;
  const isInterviewing = bid.status === ENUMS.BidStatus.INTERVIEWING;
  const isAfterAiInterview =
    bid.status === ENUMS.BidStatus.LIVE_INTERVIEW_AFTER_AI_INTERVIEW;
  const displayDeclineAction =
    bid.status !== ENUMS.BidStatus.REJECTED &&
    bid.status !== ENUMS.BidStatus.HIRED &&
    !job.completed_at &&
    job.openings_number > job.hired_bids_count;
  const displayUnRejectAction = bid.status === ENUMS.BidStatus.REJECTED;
  const displayMakeOfferAction =
    includeMakeOfferAction &&
    !bid.non_supported_location &&
    bid.can_make_offer &&
    (bid.status === ENUMS.BidStatus.NEW ||
      bid.status === ENUMS.BidStatus.VIEWED);
  const makeOfferUrl = getMakeOfferUrl({ jobId: job.id, bidId: bid.id });
  const hasProfile = !isPublicBidWithoutUser(bid);
  const displayOptionalFees = isFreelanceHired && isNodeStaff;
  const canShareWithTeamMembers = useCanShareWithTeamMembers();
  const displayShareBid = canShareWithTeamMembers && hasProfile;
  const iDVNotRequested = !bid.freelancer_idv_status;
  const iDVFailed =
    bid.freelancer_idv_status === ENUMS.IdentityVerificationStatuses.FAILED;
  const displayRequestIDV =
    isNodeOrImpersonated && hasProfile && (iDVNotRequested || iDVFailed);
  const displayMarkAsHired =
    isNodeOrImpersonated &&
    (bid.status === ENUMS.BidStatus.OFFER_UPDATED ||
      bid.status === ENUMS.BidStatus.OFFER_MADE);

  const displayAddMatcherBadgeAction =
    isNodeStaff &&
    hasProfile &&
    (bid.freelancer.approved ||
      job.job_type === ENUMS.JobType.GRANT ||
      bid.is_skipped_approval_bid);

  const [updateMatcherRecommendation] =
    useUpdateMatcherRecommendationMutation();
  const [requestIDV] = useRequestIDVMutation();
  const displaySendSmsAction =
    isNodeStaff && hasProfile && user?.freelancer !== bid.freelancer.id;

  const { refetchBidList, fetchingBids } = useBidsContext();

  const [unRejectBid] = useUnRejectBidMutation();

  const { handleHideProposal } = useChangeProposalSection();
  const { submitConfirmedLink } = useSubmitSchedulingLink({
    bid,
    job,
  });

  const handleSendCalendarClick = () => {
    submitConfirmedLink();
  };

  if (!refetchBidList) return null;

  return (
    <>
      <Menu
        anchor={
          <IconButton
            sx={{
              width: "42px",
              height: "42px",
              ...sx,
            }}
            aria-label="more actions"
            variant="white-violet"
            shape="squared"
          >
            <MoreVertIcon />
          </IconButton>
        }
      >
        {hasProfile && (
          <Menu.Item
            href={`/talent/${bid.freelancer.id}`}
            component={RouterLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            View profile
          </Menu.Item>
        )}
        {displayShareBid && (
          <Menu.Item
            onClick={() => {
              dispatch(shareApplicantClicked());
              openShareBidsWithTeamMembersModal({
                jobOwnerId: job.creator?.id,
                bidIds: [bid.id],
              });
            }}
          >
            Share applicant
          </Menu.Item>
        )}
        {displayMakeOfferAction && (
          <Menu.Item
            onClick={() => {
              if (index) {
                setIndexToScrollTo(index);
              }
              if (!!closeDrawer) closeDrawer();
              openMakeOfferMessageModal({
                bid,
                nextUrl: makeOfferUrl,
              });
            }}
          >
            Make offer
          </Menu.Item>
        )}

        {(isInterviewing || isAfterAiInterview) &&
          (!bid.interview || bid.interview.status === "round_2") && (
            <Menu.Item onClick={handleSendCalendarClick}>
              Send my calendar
            </Menu.Item>
          )}
        {displayDeclineAction && (
          <Menu.Item
            onClick={() => {
              if (index) {
                setIndexToScrollTo(index);
              }
              if (!!closeDrawer) closeDrawer();
              openRejectOfferMessageModal({
                bids: [bid],
                nextUrl: `/jobs/${job.id}/proposals/${bid.id}/rejection_feedback`,
                refetchBidList,
                job,
              });
            }}
          >
            Send rejection
          </Menu.Item>
        )}
        {displayUnRejectAction && (
          <Menu.Item
            disabled={!bid.can_unreject}
            tooltipText={bid.can_unreject_explanation || undefined}
            onClick={() => {
              unRejectBid({ bidId: bid.id })
                .unwrap()
                .then(() => refetchBidList())
                .catch((error) => {
                  const errorMessage = error?.data[0] || "Un-rejection failed.";
                  Snackbar.error(errorMessage);
                });
            }}
          >
            Un-reject
          </Menu.Item>
        )}
        {displayMarkAsHired && (
          <Menu.Item
            onClick={() => openMarkAsHiredConfirmationModal({ bidId: bid.id })}
          >
            Hired
          </Menu.Item>
        )}
        {!isFreelanceHired && !bid.is_hidden && (
          <Menu.Item
            onClick={async () =>
              await hideApplication(bid.id, refetchBidList, handleHideProposal)
            }
          >
            Hide
            <ThumbDownEmojiIcon sx={{ fontSize: 17, marginLeft: "5px" }} />
          </Menu.Item>
        )}
        {displayOptionalFees && (
          <Menu.Item
            onClick={() => openOptionalFeesModal({ bid, refetchBidList })}
            disabled={fetchingBids}
          >
            Optional fees
          </Menu.Item>
        )}
        {displayAddMatcherBadgeAction && (
          <Menu.Item
            onClick={() => {
              void updateMatcherRecommendation({
                bidId: bid.id,
                isRecommended: !bid.recommended_by_matcher,
              });
            }}
          >
            {bid.recommended_by_matcher ? "Remove" : "Add"} Matcher badge
          </Menu.Item>
        )}
        {(bid.status === "interviewing" ||
          bid.status === "in_review_after_ai_interview") &&
          bid.interview?.status === "round_1" &&
          job.is_open && (
            <Menu.Item
              onClick={() =>
                openSendRetryConfirmationModal({
                  bidId: bid.id,
                })
              }
            >
              Send AI interview retry
            </Menu.Item>
          )}
        {displayRequestIDV && (
          <Menu.Item
            onClick={() => {
              requestIDV({ bidId: bid.id })
                .unwrap()
                .then(() => {
                  Snackbar.success("IDV request sent successfully.");
                })
                .catch(() => {
                  Snackbar.error("Something went wrong.");
                });
            }}
          >
            Send IDV
          </Menu.Item>
        )}
        {displaySendSmsAction && (
          <SendSMSMessageItem freelancerId={bid.freelancer.id} />
        )}
      </Menu>
    </>
  );
};
